// ===========================================
// COMPONENTE: CuentasFiltros
// ===========================================
// Barra de filtros compacta para la tabla de cuentas

import * as React from 'react'
import { Search, X, SlidersHorizontal } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { TIPOS_CUENTA_OPTIONS, MONEDAS_OPTIONS } from './forms/cuentaSchema'

export interface CuentasFiltrosState {
  busqueda: string
  tipo: string
  institucion: string
  moneda: string
  mostrarInactivas: boolean
}

interface CuentasFiltrosProps {
  filtros: CuentasFiltrosState
  onChange: (filtros: CuentasFiltrosState) => void
  institucionesDisponibles: string[]
  totalInactivas: number
  className?: string
}

export function CuentasFiltros({
  filtros,
  onChange,
  institucionesDisponibles,
  totalInactivas,
  className,
}: CuentasFiltrosProps) {
  const [moreFiltersOpen, setMoreFiltersOpen] = React.useState(false)

  // Contar filtros activos (excluyendo búsqueda y toggle inactivas)
  const filtrosActivos = React.useMemo(() => {
    let count = 0
    if (filtros.tipo && filtros.tipo !== 'todos') count++
    if (filtros.institucion && filtros.institucion !== 'todos') count++
    if (filtros.moneda && filtros.moneda !== 'todos') count++
    return count
  }, [filtros])

  // Filtros secundarios activos (los que están en "Más filtros")
  const filtrosSecundariosActivos = React.useMemo(() => {
    let count = 0
    if (filtros.institucion && filtros.institucion !== 'todos') count++
    if (filtros.moneda && filtros.moneda !== 'todos') count++
    return count
  }, [filtros])

  // Handler para cambiar un campo
  const handleChange = (campo: keyof CuentasFiltrosState, valor: string | boolean) => {
    onChange({ ...filtros, [campo]: valor })
  }

  // Handler para limpiar todos los filtros
  const handleLimpiarFiltros = () => {
    onChange({
      busqueda: '',
      tipo: 'todos',
      institucion: 'todos',
      moneda: 'todos',
      mostrarInactivas: false,
    })
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Fila única: Búsqueda + Filtros principales + Más filtros */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Búsqueda */}
        <div className="relative min-w-[180px] flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Buscar cuentas por nombre o institución"
            placeholder="Buscar por nombre, institución..."
            value={filtros.busqueda}
            onChange={(e) => handleChange('busqueda', e.target.value)}
            className="pl-8 h-9"
          />
        </div>

        {/* Tipo - siempre visible */}
        <Select
          value={filtros.tipo}
          onValueChange={(v) => handleChange('tipo', v)}
        >
          <SelectTrigger className="w-[140px] h-9">
            <span className="truncate">
              <span className="text-muted-foreground">Tipo:</span>{' '}
              {filtros.tipo === 'todos'
                ? 'Todos'
                : TIPOS_CUENTA_OPTIONS.find((t) => t.value === filtros.tipo)?.label}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            {TIPOS_CUENTA_OPTIONS.map((tipo) => (
              <SelectItem key={tipo.value} value={tipo.value}>
                {tipo.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Más filtros (Institución, Moneda) */}
        <Popover open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                'h-9 gap-1.5',
                filtrosSecundariosActivos > 0 && 'border-primary text-primary'
              )}
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="hidden sm:inline">Más filtros</span>
              {filtrosSecundariosActivos > 0 && (
                <Badge variant="secondary" className="h-5 px-1.5 text-xs">
                  {filtrosSecundariosActivos}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3" align="start">
            <div className="space-y-3">
              <p className="text-sm font-medium">Filtros adicionales</p>

              {/* Institución */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Institución</label>
                <Select
                  value={filtros.institucion}
                  onValueChange={(v) => handleChange('institucion', v)}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas</SelectItem>
                    {institucionesDisponibles.map((inst) => (
                      <SelectItem key={inst} value={inst}>
                        {inst}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Moneda */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">Moneda</label>
                <Select
                  value={filtros.moneda}
                  onValueChange={(v) => handleChange('moneda', v)}
                >
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas</SelectItem>
                    {MONEDAS_OPTIONS.map((mon) => (
                      <SelectItem key={mon.value} value={mon.value}>
                        {mon.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Limpiar filtros secundarios */}
              {filtrosSecundariosActivos > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onChange({
                      ...filtros,
                      institucion: 'todos',
                      moneda: 'todos',
                    })
                  }}
                >
                  Limpiar estos filtros
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Toggle inactivas */}
        <div className="flex items-center gap-2">
          <Switch
            id="mostrar-inactivas"
            checked={filtros.mostrarInactivas}
            onCheckedChange={(checked) => handleChange('mostrarInactivas', checked)}
          />
          <label
            htmlFor="mostrar-inactivas"
            className="text-sm cursor-pointer whitespace-nowrap"
          >
            Inactivas ({totalInactivas})
          </label>
        </div>

        {/* Limpiar todo (solo si hay filtros activos) */}
        {filtrosActivos > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLimpiarFiltros}
            className="h-9 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Limpiar</span>
          </Button>
        )}
      </div>

      {/* Badges de filtros activos */}
      {filtrosActivos > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-muted-foreground">Filtros:</span>
          {filtros.tipo !== 'todos' && (
            <Badge variant="secondary" className="gap-1 pr-1 h-6">
              Tipo: {filtros.tipo}
              <button
                type="button"
                onClick={() => handleChange('tipo', 'todos')}
                aria-label={`Quitar filtro de tipo ${filtros.tipo}`}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filtros.institucion !== 'todos' && (
            <Badge variant="secondary" className="gap-1 pr-1 h-6">
              {filtros.institucion}
              <button
                type="button"
                onClick={() => handleChange('institucion', 'todos')}
                aria-label={`Quitar filtro de institución ${filtros.institucion}`}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {filtros.moneda !== 'todos' && (
            <Badge variant="secondary" className="gap-1 pr-1 h-6">
              {filtros.moneda}
              <button
                type="button"
                onClick={() => handleChange('moneda', 'todos')}
                aria-label={`Quitar filtro de moneda ${filtros.moneda}`}
                className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}

